import React from 'react'
import toast from 'react-hot-toast'
import { Icon } from '../shared'
import { Btn } from '../shared/components'
import { DocUploader } from '../shared/FileUpload'
import { uploadBrandingImage, updateSettings } from '../api/settings.api'
import { HEX, onColor, contrast } from '../lib/color'
import { brandVars } from '../lib/brandTheme'
import { useTheme } from '../lib/theme'
import LoginShell from './LoginShell'

/* ============================================================
   Bagian layar Pengaturan untuk halaman masuk lembaga: unggah gambar,
   warna, tata letak, dan pratinjau langsung.

   Server yang memutuskan apa yang termasuk paket (routes/settings.js); layar
   ini hanya menampilkan kuncinya dengan jelas — kontrol di atas paket
   dinonaktifkan dan memberi tahu paket apa yang dibutuhkan — supaya admin tidak
   mengisi sesuatu yang lalu ditolak saat Simpan.
   ============================================================ */

const tr = (lang, id, en) => (lang === 'id' ? id : en)

const MB = 1024 * 1024
const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'webp']

// Sama dengan ProgramManageScreen: palet kecil yang sudah dipilih agar serasi.
export const PALET = ['#0F62FE', '#1F7A5C', '#7A6A45', '#B84E2A', '#6B4FA0', '#3E4A50', '#B53A2C', '#0E7C86']

/** Penanda kunci: kontrol ini butuh paket yang lebih tinggi. */
export function PlanLock({ need, lang }) {
  return (
    <span
      className="tiny"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8,
        padding: '1px 8px', borderRadius: 999, fontWeight: 500,
        background: 'var(--surface-3)', color: 'var(--text-2)',
      }}
    >
      <Icon name="lock" size={10} />
      {tr(lang, `Tersedia di ${need}`, `Available on ${need}`)}
    </span>
  )
}

// Kotak penyusun satu kontrol: label (+ kunci), isi, keterangan.
export function Row({ label, htmlFor, lock, lang, hint, children }) {
  return (
    <div className="tile" style={{ padding: '20px 24px' }}>
      <label htmlFor={htmlFor} className="small" style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
        {label}
        {lock && <PlanLock need={lock} lang={lang} />}
      </label>
      {children}
      {hint && <div className="tiny muted mt-2">{hint}</div>}
    </div>
  )
}

// ── Gambar: unggah, ganti, hapus ──────────────────────────────
const SHAPES = {
  logo: { w: 56, h: 56, radius: '50%', fit: 'cover' },
  favicon: { w: 40, h: 40, radius: 8, fit: 'contain' },
  cover: { w: 128, h: 72, radius: 8, fit: 'cover' },
}

export function ImageField({ kind, field, label, hint, maxBytes, accept = IMAGE_EXT, value, lock, locked, lang, onSaved }) {
  const shape = SHAPES[kind]
  const [broken, setBroken] = React.useState(false)
  const [removing, setRemoving] = React.useState(false)
  React.useEffect(() => setBroken(false), [value])

  async function remove() {
    setRemoving(true)
    try {
      onSaved(await updateSettings({ [field]: '' }))
      toast.success(tr(lang, 'Gambar dihapus', 'Image removed'))
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    } finally {
      setRemoving(false)
    }
  }

  return (
    <Row label={label} lock={lock} lang={lang} hint={hint}>
      {locked ? (
        <div className="small muted">
          {tr(lang, 'Tersedia setelah paket dinaikkan. Hubungi pengelola platform.', 'Available after upgrading. Contact the platform operator.')}
        </div>
      ) : (
        <div className="col gap-3">
          {value && (
            <div className="row gap-3" style={{ alignItems: 'center' }}>
              <div style={{
                width: shape.w, height: shape.h, flexShrink: 0, overflow: 'hidden',
                borderRadius: shape.radius, background: 'var(--surface-2)', border: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {broken
                  ? <Icon name="doc" size={16} color="var(--text-3)" />
                  : <img src={value} alt="" onError={() => setBroken(true)}
                      style={{ width: '100%', height: '100%', objectFit: shape.fit, display: 'block' }} />}
              </div>
              <Btn type="button" variant="ghost" size="sm" onClick={remove} disabled={removing}
                icon={<Icon name="close" size={12} />}>
                {tr(lang, 'Hapus', 'Remove')}
              </Btn>
            </div>
          )}
          <DocUploader
            accept={accept}
            maxBytes={maxBytes}
            upload={(file, opts) => uploadBrandingImage(kind, file, opts).then((b) => { onSaved(b); return b })}
            successMessage={() => tr(lang, 'Gambar diunggah', 'Image uploaded')}
          />
        </div>
      )}
    </Row>
  )
}

export const IMAGE_LIMITS = { logo: 1 * MB, favicon: 1 * MB, cover: 5 * MB }

// ── Warna utama ───────────────────────────────────────────────
export function ColorField({ value, onChange, lang }) {
  const valid = HEX.test(value || '')
  const typed = !!value && !valid
  // Peringatan yang lebih ketat daripada aturan tombol (3:1, lihat onColor):
  // 4.5:1 adalah batas WCAG untuk teks biasa. Hanya saran — tidak memblokir.
  const ratio = valid ? contrast(onColor(value), value) : null

  return (
    <div className="col gap-3">
      <div className="row gap-2" style={{ flexWrap: 'wrap', alignItems: 'center' }} role="radiogroup"
        aria-label={tr(lang, 'Warna utama', 'Primary colour')}>
        {PALET.map((c) => (
          <button key={c} type="button" role="radio" aria-checked={valid && value.toLowerCase() === c.toLowerCase()}
            aria-label={c} title={c} onClick={() => onChange(c)}
            style={{
              width: 26, height: 26, borderRadius: '50%', background: c, cursor: 'pointer',
              border: valid && value.toLowerCase() === c.toLowerCase() ? '3px solid var(--text)' : '2px solid var(--surface)',
              outline: '1px solid var(--border-strong)',
            }} />
        ))}
        <label className="row gap-1 tiny muted" style={{ alignItems: 'center', cursor: 'pointer' }}>
          <input type="color" value={valid ? value : PALET[0]} onChange={(e) => onChange(e.target.value)}
            aria-label={tr(lang, 'Warna lain', 'Custom colour')}
            style={{ width: 28, height: 28, padding: 0, border: 0, background: 'none', cursor: 'pointer' }} />
          {tr(lang, 'lainnya', 'custom')}
        </label>
      </div>
      <div className="row gap-2" style={{ alignItems: 'center' }}>
        <input
          id="setelan-primary_color"
          name="primary_color"
          className="input"
          style={{ width: 130 }}
          value={value || ''}
          maxLength={7}
          placeholder="#0E7C86"
          aria-invalid={typed}
          onChange={(e) => onChange(e.target.value.trim())}
        />
        {value && (
          <Btn type="button" variant="ghost" size="sm" onClick={() => onChange('')}>
            {tr(lang, 'Pakai warna bawaan', 'Use default')}
          </Btn>
        )}
      </div>
      {typed && (
        <div role="alert" className="tiny" style={{ color: 'var(--danger)' }}>
          {tr(lang, 'Format warna harus #RRGGBB, mis. #0E7C86.', 'Colour must be #RRGGBB, e.g. #0E7C86.')}
        </div>
      )}
      {ratio != null && ratio < 4.5 && (
        <div className="tiny" style={{ color: 'var(--amber)' }}>
          {tr(lang,
            `Teks tombol di warna ini berkontras ${ratio.toFixed(1)}:1; disarankan minimal 4.5:1 agar mudah dibaca. Coba warna yang lebih gelap atau lebih terang.`,
            `Button text on this colour has ${ratio.toFixed(1)}:1 contrast; 4.5:1 or more is recommended for readability. Try a darker or lighter colour.`)}
        </div>
      )}
    </div>
  )
}

// ── Tata letak ────────────────────────────────────────────────
const LAYOUTS = [
  { key: 'SPLIT', id: 'Terbagi', en: 'Split', hintId: 'Gambar di kiri, formulir di kanan', hintEn: 'Image left, form right' },
  { key: 'FULL', id: 'Layar penuh', en: 'Full screen', hintId: 'Gambar memenuhi layar, formulir di kartu', hintEn: 'Image fills the screen, form on a card' },
  { key: 'PLAIN', id: 'Polos', en: 'Plain', hintId: 'Hanya formulir', hintEn: 'Form only' },
]

function LayoutGlyph({ kind }) {
  const stroke = 'var(--text-3)'
  const fill = 'var(--program-color)'
  return (
    <svg viewBox="0 0 64 40" width="64" height="40" aria-hidden="true">
      <rect x="1" y="1" width="62" height="38" rx="4" fill="var(--surface)" stroke={stroke} />
      {kind === 'SPLIT' && <><rect x="1" y="1" width="30" height="38" rx="4" fill={fill} opacity="0.85" />
        <rect x="38" y="14" width="19" height="4" rx="2" fill={stroke} /><rect x="38" y="22" width="19" height="5" rx="2" fill={fill} /></>}
      {kind === 'FULL' && <><rect x="1" y="1" width="62" height="38" rx="4" fill={fill} opacity="0.85" />
        <rect x="20" y="9" width="24" height="22" rx="3" fill="var(--surface)" /><rect x="24" y="22" width="16" height="4" rx="2" fill={fill} /></>}
      {kind === 'PLAIN' && <><rect x="20" y="11" width="24" height="3" rx="1.5" fill={stroke} />
        <rect x="20" y="18" width="24" height="5" rx="2" fill="var(--surface-3)" /><rect x="20" y="26" width="24" height="5" rx="2" fill={fill} /></>}
    </svg>
  )
}

export function LayoutPicker({ value, onChange, disabled, lang }) {
  return (
    <div className="row gap-2" style={{ flexWrap: 'wrap' }} role="radiogroup" aria-label={tr(lang, 'Tata letak', 'Layout')}>
      {LAYOUTS.map((l) => {
        const on = (value || 'SPLIT') === l.key
        return (
          <button key={l.key} type="button" role="radio" aria-checked={on} disabled={disabled}
            onClick={() => onChange(l.key)}
            title={tr(lang, l.hintId, l.hintEn)}
            style={{
              flex: '1 1 120px', minWidth: 110, padding: '10px 8px', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
              background: 'var(--surface-2)', color: 'var(--text)', opacity: disabled ? 0.55 : 1,
              border: on ? '2px solid var(--program-color)' : '1px solid var(--border-strong)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            }}>
            <LayoutGlyph kind={l.key} />
            <span className="small" style={{ fontWeight: on ? 700 : 500 }}>{tr(lang, l.id, l.en)}</span>
          </button>
        )
      })}
    </div>
  )
}

// ── Pratinjau langsung ────────────────────────────────────────
// Memakai LoginShell yang sama dengan halaman masuk sungguhan, jadi yang dilihat
// admin adalah yang akan dilihat peserta — termasuk isian yang belum disimpan.
// Dirender pada ukuran layar sebenarnya lalu diperkecil, supaya titik putus
// tata letak (HP/desktop) berperilaku seperti aslinya.
export function LoginPreview({ draft, lang }) {
  const { resolved: theme } = useTheme()
  const [device, setDevice] = React.useState('desktop')
  const wrapRef = React.useRef(null)
  const [width, setWidth] = React.useState(0)

  React.useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const mobile = device === 'mobile'
  const baseW = mobile ? 375 : 1100
  const baseH = mobile ? 720 : 640
  const scale = width ? Math.min(1, width / baseW) : 1

  // Warna merek hanya berlaku di dalam kotak pratinjau, bukan seluruh aplikasi.
  const vars = brandVars(draft.primary_color, theme)
  if (vars['--program-color']) {
    // Di :root, --program-color-text diturunkan dari variabel lain sehingga tidak
    // ikut berubah bila yang ditimpa hanya variabel dasarnya; tulis ulang di sini.
    vars['--program-color-text'] = theme === 'dark'
      ? `color-mix(in srgb, ${vars['--program-color']} 50%, white)`
      : vars['--program-color-deep']
  }

  return (
    <div className="tile" style={{ padding: '16px 20px' }}>
      <div className="row between" style={{ alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div className="small" style={{ fontWeight: 600 }}>{tr(lang, 'Pratinjau Halaman Masuk', 'Sign-in Page Preview')}</div>
        <div className="row gap-1" role="radiogroup" aria-label={tr(lang, 'Ukuran layar', 'Screen size')}>
          {[['desktop', 'Desktop'], ['mobile', tr(lang, 'HP', 'Phone')]].map(([k, text]) => (
            <Btn key={k} type="button" size="sm" variant={device === k ? 'primary' : 'ghost'}
              role="radio" aria-checked={device === k} onClick={() => setDevice(k)}>
              {text}
            </Btn>
          ))}
        </div>
      </div>

      {/* Kerangka diukur, isinya diposisikan absolut: isi yang lebar tidak boleh
          ikut melebarkan kerangka, atau skalanya tidak pernah turun di layar
          sempit (ukuran kerangka ditentukan isi, isi ditentukan skala). */}
      <div ref={wrapRef} style={{
        position: 'relative', width: '100%', height: baseH * scale, overflow: 'hidden',
        borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface-2)',
      }}>
        <div aria-hidden="true" style={{
          position: 'absolute', top: 0, left: Math.max(0, (width - baseW * scale) / 2),
          width: baseW, height: baseH, transform: `scale(${scale})`, transformOrigin: 'top left',
          pointerEvents: 'none', overflow: 'hidden', ...vars,
        }}>
          <LoginShell branding={draft} lang={lang} setLang={() => {}} isMobile={mobile} minHeight={baseH}>
            <div className="eyebrow">SIGN IN</div>
            <h2 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', marginTop: 6 }}>
              {tr(lang, 'Masuk ke akun Anda', 'Sign in to your account')}
            </h2>
            <p className="muted mt-2" style={{ fontSize: 14 }}>{draft.tagline || ''}</p>
            <div className="col gap-3" style={{ marginTop: 16 }}>
              <div className="small" style={{ fontWeight: 500 }}>{tr(lang, 'Email atau kode kelas', 'Email or class code')}</div>
              <div className="input" style={{ height: 40, display: 'flex', alignItems: 'center', color: 'var(--text-3)' }}>
                email@perusahaan.com
              </div>
              <div className="btn-primary btn-lg btn-block" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {tr(lang, 'Lanjut →', 'Continue →')}
              </div>
            </div>
          </LoginShell>
        </div>
      </div>
      <div className="tiny muted mt-2">
        {tr(lang,
          'Menampilkan isian yang belum disimpan. Elemen di atas paket Anda tidak ikut tampil.',
          'Shows unsaved changes. Elements above your plan are not shown.')}
      </div>
    </div>
  )
}
